import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Table } from 'core/components/table';
import { last as _last, max as _max, mean as _mean, sum } from 'lodash';
import { protoTimestampToDate } from 'core/util/dateUtils';
import { numberWithCommas } from 'app/util/utils';
import TimeSelector from 'app/views/core/explorer/sidebar/TimeSelector';
import { Box, Card, Divider, Flex, FlexColumn, Grid, Heading, Highcharts, Text } from 'core/components';
import $kaizen from 'app/stores/ai/$kaizen';
import SettingsPanelHeading from 'app/views/settings/ai/SettingsPanelHeading';

const MS_PER_DAY = 86400000;
const S_PER_DAY = 86400;

const LOOKBACK_7D = 7 * S_PER_DAY;
const LOOKBACK_30D = 30 * S_PER_DAY;
const LOOKBACK_90D = 90 * S_PER_DAY;
const LOOKBACK_1Y = 365 * S_PER_DAY;
const LOOKBACK_MTD = 1;

const TIME_RANGE_OPTIONS = [
  { label: 'Month to Date', value: LOOKBACK_MTD },
  { label: 'Last 7 Days', value: LOOKBACK_7D },
  { label: 'Last 30 Days', value: LOOKBACK_30D },
  { label: 'Last 90 Days', value: LOOKBACK_90D },
  { label: 'Last 12 Months', value: LOOKBACK_1Y },
  { label: 'Custom', value: 0 }
];

const TIME_RANGE_CONFIG = {
  [LOOKBACK_MTD]: { getMtdDays: true },
  [LOOKBACK_7D]: { days: 7 },
  [LOOKBACK_30D]: { days: 30 },
  [LOOKBACK_90D]: { days: 90 },
  [LOOKBACK_1Y]: { months: 12 }
};

const QUESTION_USER_COLUMNS = [
  { label: 'User', name: 'user_email', flexBasis: 200 },
  { label: 'Questions', name: 'count', width: 100 }
];

function getInvestigationPolicyColumns(startTime, endTime) {
  return [
    { label: 'Alerting Policy', name: 'policy', flexBasis: 200, renderer: ({ model }) => model.policy?.name ?? '—' },
    {
      label: 'Investigations',
      name: 'investigations',
      width: 120,
      renderer: ({ model }) => {
        const investigations = model.get('investigations') ?? [];
        if (!startTime || !endTime) {
          return investigations.length;
        }
        return investigations.filter((inv) => {
          const d = protoTimestampToDate(inv.created_at);
          const ts = d?.getTime();
          return ts && !Number.isNaN(ts) && ts / 1000 >= startTime && ts / 1000 <= endTime;
        }).length;
      }
    }
  ];
}

// Shared by the chart and table grids so a lone card in either grid fills the full row.
const CARD_GRID_TEMPLATE_COLUMNS = 'repeat(auto-fit, minmax(max(480px, calc(50% - 8px)), 1fr))';

const BASE_CHART = {
  chart: { type: 'column', reflow: true },
  credits: { enabled: false },
  legend: { enabled: false },
  xAxis: { type: 'datetime' },
  yAxis: { title: { text: null }, min: 0, allowDecimals: false },
  tooltip: { shared: true, shape: 'callout', xDateFormat: '%b %e, %Y' }
};

function getStartTimeSeconds(rangeKey) {
  const config = TIME_RANGE_CONFIG[rangeKey];
  const now = new Date();

  if (config.getMtdDays) {
    now.setDate(1);
  } else if (config.months) {
    now.setMonth(now.getMonth() - config.months);
  } else {
    now.setDate(now.getDate() - config.days);
  }

  now.setHours(0, 0, 0, 0);
  return Math.floor(now.getTime() / 1000);
}

function getBucketStartTimes(rangeKey, customStart, customEnd) {
  let startOfRange;
  let bucketCount;

  if (rangeKey === 0 && customStart && customEnd) {
    startOfRange = new Date(customStart * 1000);
    bucketCount = Math.max(Math.ceil((customEnd - customStart) / S_PER_DAY), 1);
  } else {
    const startTime = getStartTimeSeconds(rangeKey);
    if (!startTime) {
      return [];
    }
    startOfRange = new Date(startTime * 1000);
    bucketCount = Math.max(Math.ceil((Date.now() - startOfRange) / MS_PER_DAY), 1);
  }

  startOfRange.setHours(0, 0, 0, 0);
  return Array.from({ length: bucketCount }, (_, i) => {
    const d = new Date(startOfRange);
    d.setDate(d.getDate() + i);
    return d.getTime();
  });
}

function aggregateModels(models, getDate, rangeKey, customStart, customEnd) {
  const bucketStarts = getBucketStartTimes(rangeKey, customStart, customEnd);
  if (!bucketStarts.length) {
    return [];
  }

  const counts = new Array(bucketStarts.length).fill(0);
  const startOfRange = new Date(bucketStarts[0]);
  const endOfRange = new Date(startOfRange);
  endOfRange.setDate(endOfRange.getDate() + bucketStarts.length);

  models.forEach((model) => {
    const d = getDate(model);
    if (!d || Number.isNaN(d.getTime()) || d < startOfRange || d >= endOfRange) {
      return;
    }
    counts[Math.floor((d - startOfRange) / MS_PER_DAY)] += 1;
  });

  return bucketStarts.map((ts, i) => [ts, counts[i]]);
}

const StatsRow = ({ data, loading }) => {
  if (loading) {
    return <Text muted>—</Text>;
  }

  const counts = data.map((d) => d[1]);
  const stats = [
    ['Avg', numberWithCommas(Math.round(counts.length ? _mean(counts) : 0))],
    ['Max', numberWithCommas(counts.length ? _max(counts) : 0)],
    ['Last', numberWithCommas(counts.length ? _last(counts) : 0)],
    ['Total', numberWithCommas(sum(counts))]
  ];

  return (
    <Flex gap={3}>
      {stats.map(([label, value]) => (
        <Flex key={label} flexDirection="column" alignItems="flex-end">
          <Text fontSize={11} muted>
            {label}
          </Text>
          <Text fontSize={16} fontWeight="bold">
            {value}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

const ChartCard = ({ title, statsRow, chartOptions, chartProps }) => (
  <Card p={0}>
    <Flex justifyContent="space-between" alignItems="center" p={3} flexWrap="wrap">
      <Heading level={5} m={0}>
        {title}
      </Heading>
      {statsRow}
    </Flex>
    <Box px={3} pb={3}>
      <Highcharts options={chartOptions} {...chartProps} />
    </Box>
  </Card>
);

const TableCard = ({ title, collection, columns, loading }) => (
  <Card display="flex" flexDirection="column" overflow="hidden" p={0} height={300}>
    <Box p={3}>
      <Heading level={5} m={0}>
        {title}
      </Heading>
    </Box>
    <Divider />
    <Box flex={1} overflow="auto">
      <Table
        collection={collection}
        columns={columns}
        selectOnRowClick={false}
        fetchCollectionOnMount={false}
        loading={loading}
        stickyHeader
      />
    </Box>
  </Card>
);

@inject('$ai', '$colors', '$investigationConfig', '$auth')
@observer
class AdvisorUsage extends Component {
  state = { lookbackSeconds: LOOKBACK_1Y, customStartDate: null, customEndDate: null };

  get hasAutoInvestigationPermission() {
    const { $auth } = this.props;
    return $auth.hasPermission('aiInvestigation.enabled', { overrideForSudo: false });
  }

  get timeBounds() {
    const { lookbackSeconds: l, customStartDate: s, customEndDate: e } = this.state;
    const timeRange = l || 0;
    if (timeRange === 0 && s && e) {
      return [s, e];
    }
    if (TIME_RANGE_CONFIG[timeRange]) {
      return [getStartTimeSeconds(timeRange), Math.floor(Date.now() / 1000)];
    }
    return [null, null];
  }

  componentDidMount() {
    this.updateUsage();
  }

  componentDidUpdate(prevProps, prevState) {
    const { lookbackSeconds, customStartDate, customEndDate } = this.state;
    if (
      lookbackSeconds !== prevState.lookbackSeconds ||
      customStartDate !== prevState.customStartDate ||
      customEndDate !== prevState.customEndDate
    ) {
      this.updateUsage();
    }
  }

  updateUsage = () => {
    const { $ai, $investigationConfig } = this.props;
    const [startTime, endTime] = this.timeBounds;
    const { lookbackSeconds } = this.state;
    if (!startTime || !endTime) {
      return;
    }

    // InvestigationConfigCollection.fetch short-circuits to empty without the aiInvestigation permission.
    const configFetch = $investigationConfig.collection.fetch(
      lookbackSeconds === 0 ? { start_time: startTime, end_time: endTime } : { start_time: startTime }
    );
    const sessionFetch = $kaizen.isEnabled
      ? $kaizen.chatSessionCollection.fetchPromise || $kaizen.chatSessionCollection.fetch({ preserveSelection: true })
      : Promise.resolve();

    Promise.all([configFetch, sessionFetch])
      .then(() => $ai.updateUserQuestionCollection({ startTime, endTime }))
      .catch((error) => console.error('Failed to fetch usage data:', error));
  };

  handleTimeChange = (value) => {
    if (value.lookbackSeconds === 0) {
      this.setState({
        lookbackSeconds: 0,
        customStartDate: value.startDate ? Math.floor(value.startDate.getTime() / 1000) : null,
        customEndDate: value.endDate ? Math.floor(value.endDate.getTime() / 1000) : null
      });
    } else {
      this.setState({ lookbackSeconds: value.lookbackSeconds, customStartDate: null, customEndDate: null });
    }
  };

  render() {
    const { $ai, $colors, $investigationConfig } = this.props;
    const { lookbackSeconds, customStartDate, customEndDate } = this.state;
    const timeRange = lookbackSeconds || 0;
    const [startTime, endTime] = this.timeBounds;
    const { hasAutoInvestigationPermission } = this;

    const isLoading =
      (hasAutoInvestigationPermission && $investigationConfig.collection.isRequestActive('fetching')) ||
      $ai.userQuestionCollection?.isRequestActive?.('fetching') ||
      !$ai.userQuestionCollection?.hasFetched;

    const sessions = ($ai.store?.$kaizen?.chatSessionCollection?.models ?? []).filter((s) => !s.isAutomatedChat);
    const questionsData = aggregateModels(
      sessions,
      (s) => new Date(s.get('created_at')),
      timeRange,
      customStartDate,
      customEndDate
    );
    // Investigation data only renders when the permission is present; skip the work on the shared
    // $investigationConfig singleton (which other views may have populated) when it won't be shown.
    const investigationsData = hasAutoInvestigationPermission
      ? aggregateModels(
          $investigationConfig.collection.models.flatMap((m) => m.get('investigations') ?? []),
          (inv) => protoTimestampToDate(inv.created_at),
          timeRange,
          customStartDate,
          customEndDate
        )
      : [];
    const chartProps = { allowChartUpdate: true, colors: $colors.chartColors, height: 200 };

    return (
      <FlexColumn flex={1} minHeight={0} overflow="auto" pt={4} px={4}>
        <Flex justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" mb="16px">
          <SettingsPanelHeading level={2} m={0}>
            AI Advisor Usage
          </SettingsPanelHeading>
          {/* stays on the right when the heading is hidden */}
          <Box ml="auto">
            <TimeSelector
              lookbackOptions={TIME_RANGE_OPTIONS}
              lookbackSeconds={lookbackSeconds}
              onChange={this.handleTimeChange}
            />
          </Box>
        </Flex>

        {/* Charts and tables sit in separate grids so a lone card (e.g. Questions alone, when
            Investigations is hidden) fills the full row instead of being forced into a 50/50
            split next to an unrelated card type. */}
        <Grid gridColumnGap="16px" gridRowGap="16px" gridTemplateColumns={CARD_GRID_TEMPLATE_COLUMNS}>
          <ChartCard
            title="Questions"
            chartOptions={{ ...BASE_CHART, series: [{ name: 'Questions', data: questionsData }] }}
            chartProps={chartProps}
            statsRow={<StatsRow data={questionsData} />}
          />
          {hasAutoInvestigationPermission && (
            <ChartCard
              title="Investigations"
              chartOptions={{ ...BASE_CHART, series: [{ name: 'Investigations', data: investigationsData }] }}
              chartProps={chartProps}
              statsRow={<StatsRow data={investigationsData} loading={isLoading} />}
            />
          )}
        </Grid>

        <Grid gridColumnGap="16px" gridRowGap="16px" gridTemplateColumns={CARD_GRID_TEMPLATE_COLUMNS} mt="16px">
          <TableCard
            title="Questions Per User — Top 50"
            collection={$ai.userQuestionCollection}
            columns={QUESTION_USER_COLUMNS}
            loading={!$ai.userQuestionCollection.hasFetched}
          />
          {hasAutoInvestigationPermission && (
            <TableCard
              title="Investigations Per Alerting Policy"
              collection={$investigationConfig.collection}
              columns={getInvestigationPolicyColumns(startTime, endTime)}
            />
          )}
        </Grid>

        <Box flexShrink={0} height={16} />
      </FlexColumn>
    );
  }
}

export default AdvisorUsage;
